import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  HostBinding,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputPresenter } from './input.presenter';
import { IMessageError, IconName } from '@core/interfaces';
import { InputTypeEnum, InputTypeEnumValues } from './enums/input-type.enum';
import { IconComponent } from '@shared/components';
import { formatPhoneNumber, PhoneMaskDirective } from '@/shared/directives';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, IconComponent, PhoneMaskDirective],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    InputPresenter,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent<T>
  implements ControlValueAccessor, OnInit, OnDestroy
{
  presenter: InputPresenter<T> = inject(InputPresenter) as InputPresenter<T>;

  @HostBinding('class.is-checkbox') get isCheckboxClass(): boolean { return this.type === 'checkbox'; }

  @Input() id?: string;
  @Input() name: string = '';
  @Input() label?: string | null;
  @Input() placeholder?: string = '';
  @Input() type: InputTypeEnumValues = InputTypeEnum.Text;
  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() control?: AbstractControl | null;
  @Input() controlId?: string;
  @Input() errorMessages?: IMessageError;
  @Input() containerClass: string = '';

  // Restored inputs from Backoffice to maintain identical logic signature
  @Input() skeleton: boolean = false;
  @Input() timePicker: boolean = false;
  @Input() prefix: string | undefined;
  @Input() suffix: string | undefined;
  @Input() mode: string = 'decimal';
  @Input() currency: string = 'USD';
  @Input() locale: string = 'en-US';
  @Input() min: number | undefined;
  @Input() max: number | undefined;
  @Input() mask: string | undefined = '000000';
  @Input() maxFractionDigits: number | undefined;
  @Input() minFractionDigits: number | undefined;
  @Input() useGrouping: boolean = false;
  @Input() floatLabel: boolean = false;
  @Input() dropdownShowClear: boolean = false;
  @Input() dropdownSearch: boolean = false;
  @Input() timePickerFormat: number = 24;
  @Input() timePickerMin: string = '00:00';
  @Input() timePickerMax: string = '23:59';
  @Input() switchName: string = '';
  @Input() contentClass: string = 'block relative';
  @Input() isRadioFlex: boolean = true;
  @Input() appendTo: string = 'body';
  @Input() rows: number = 5;
  @Input() cols: number = 30;
  @Input() customIcon?: IconName;
  @Input() tooltip?: string;
  @Input() inputStyle? : { [value: string]: string };
  @Input() showButtons: boolean = false;
  @Input() emptyDropdownMessage: string = 'No results found';
  @Input() maxLength: number | undefined;
  @Input() showButtonBarInCalendar: boolean = false;
  @Input() decimalFirstLogic: boolean = true;
  @Input() preventEmpty: boolean = false;
  @Input() virtualScrollItemSize: number = 30;
  @Input() virtualScroll: boolean = false;
  @Input() lazy: boolean = false;
  @Input() minDropdownTrigger: number = 0.66;
  @Input() minDate: Date | undefined;
  @Input() maxDate: Date | undefined;
  @Input() styleClass: string = '';
  @Input() inputStyleClass: string = '';
  @Input() customPrefix: string | undefined;
  @Input() customSuffix: string | undefined;
  @Input() errorMessageStyle: string | undefined;
  @Input() options: T[] = [];
  @Input() optionLabel?: string;
  @Input() optionValue?: string;

  @Output() inputChange = new EventEmitter<string | T>();
  @Output() dropdownChange = new EventEmitter<string>();
  @Output() calendarChange = new EventEmitter<Date>();
  @Output() switchChange = new EventEmitter<{ checked: boolean; data: T | null }>();
  @Output() elementChange = new EventEmitter<T>();
  @Output() multidropdownChange = new EventEmitter<T>();
  @Output() lazyLoadDropdownChange = new EventEmitter<unknown>();
  @Output() modelChange = new EventEmitter<string>();

  uniqueId = `input-${Math.random().toString(36).substring(2, 9)}`;
  InputTypeEnum = this.presenter.InputTypeEnum;

  // ControlValueAccessor
  private _value: string | boolean | null = null;
  private _onChange: (v: string | boolean | null) => void = () => {};
  private _onTouched: () => void = () => {};

  get value(): string | boolean | null {
    return this.type === InputTypeEnum.Phone && typeof this._value === 'string'
      ? formatPhoneNumber(this._value)
      : this._value;
  }
  set value(v: string | boolean | null) {
    this._value = v;
    this._onChange(v);
    this.inputChange.emit((v as string | T) ?? '');
  }

  writeValue(v: string | boolean | null): void { 
    this._value = v; 
  }
  registerOnChange(fn: (v: string | boolean | null) => void): void { this._onChange = fn; }
  registerOnTouched(fn: () => void): void { this._onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { 
    this.disabled = isDisabled; 
  }

  onTouched(): void {
    this._onTouched();
  }

  // Delegations to Presenter
  get formControl(): FormControl {
    return this.presenter.formControl;
  }

  get isInvalid(): boolean {
    return this.presenter.isInvalid;
  }

  get showPassword(): boolean {
    return this.presenter.showPassword;
  }

  getErrorMessage(): string | null {
    return this.presenter.getErrorMessage();
  }

  getOptionProperty(option: T, key: string): unknown {
    return this.presenter.getOptionProperty(option, key);
  }

  onInput(event: Event): void {
    this.presenter.onInput(event);
  }

  onDropdownChange(event: Event): void {
    this.presenter.onDropdownChange(event);
  }

  onPhoneValueChange(value: string): void {
    this.value = value;
  }

  onCheckboxChange(event: Event): void {
    this.presenter.onCheckboxChange(event);
  }

  togglePassword(): void {
    this.presenter.togglePassword();
  }

  onBlur(): void {
    this.presenter.onBlur();
  }

  ngOnInit(): void { this.presenter.setView(this); }
  ngOnDestroy(): void { this.presenter.onDestroy(); }
}
